import Feature from "ol/Feature";
import { LineString } from "ol/geom";
import { getMembersWithRole, getTags } from "../../util";
import {
  getSourceGeometry,
  setSourceGeometry,
} from "../../util/featureWkt.mjs";
import { groupOverlappingArcs } from "./arcOverlapGrouping.mjs";
import { computeArcCoordinates } from "./curvatureArcGeometry";
import { runDerivationPass } from "./curvatureArcScheduler";
import { turningSign } from "../style/util/polyline.mjs";

function isCajArc(feature) {
  const t = feature.get("type");
  return t === "curvature-arc" || t === "curvature-arc-dummy";
}

// Recompute overlap offsets across ALL arcs currently in the source and apply
// them. Reads geometry endpoints, defers the grouping math to the pure helper,
// and only writes when a value actually changes (avoid render thrash).
function assignOverlapOffsets(source) {
  const arcs = source.getFeatures().filter(isCajArc);
  const items = arcs.map((f, i) => {
    const coords = f.getGeometry().getCoordinates();
    return {
      key: i,
      start: coords[0],
      end: coords[coords.length - 1],
      // tightest radius (highest |curvature|) is placed on the inside of the bend.
      absCurvature: Math.abs(Number(f.get("curvature_at_junction"))) || 0,
      turnSign: turningSign(coords),
    };
  });
  const offsets = groupOverlappingArcs(items);
  arcs.forEach((f, i) => {
    const next = offsets.get(i);
    if (next === undefined) {
      if (f.get("overlap-offset") !== undefined) {
        f.unset("overlap-offset");
        f.unset("overlap-start-node");
        f.unset("overlap-end-node");
      }
      return;
    }
    if (f.get("overlap-offset") !== next.offset) {
      f.set("overlap-offset", next.offset);
    }
    setNodeIfChanged(f, "overlap-start-node", next.start);
    setNodeIfChanged(f, "overlap-end-node", next.end);
  });
}

// Set an [x, y] node property only when it actually changed (avoid render thrash).
function setNodeIfChanged(feature, key, node) {
  const cur = feature.get(key);
  if (!cur || cur[0] !== node[0] || cur[1] !== node[1]) {
    feature.set(key, node);
  }
}

// Per-source derivation state, keyed by the VectorSource. Never global — two CaJ
// layers can hold relations with the same Orbis id, and `clear` must reset only
// the cleared source. Replaces the old module-global `missingIds`.
const STATE = new WeakMap();
const dirtySources = new Set();
let rafHandle = null;
const FRAME_BUDGET_MS = 10;

// Read the from/to/via points + curvature for a relation, or report a missing
// member (the member feature hasn't arrived in this source yet). Pure read — no
// feature creation. Mirrors the original handler's extraction exactly, except
// member lookup is now O(1) via the prebuilt index instead of features.find().
function resolveArcInputs(relation, indexById) {
  const viaMember = getMembersWithRole(relation, "via")[0];
  const alongFromMember = getMembersWithRole(relation, "along_from_road")[0];
  const alongToMember = getMembersWithRole(relation, "along_to_road")[0];
  if (!viaMember || !alongFromMember || !alongToMember) {
    console.error("member not found for feature", relation);
    return { status: "missing" };
  }
  const viaFeature = indexById.get(viaMember.id);
  const alongFromFeature = indexById.get(alongFromMember.id);
  const alongToFeature = indexById.get(alongToMember.id);
  if (!viaFeature || !alongFromFeature || !alongToFeature) {
    return { status: "missing" };
  }

  const curvature = Number(
    getTags(relation).find((tag) => tag.tagKey.key === "curvature_at_junction")
      .tagValue,
  );
  const fromCoordinate = alongFromFeature
    .getGeometry()
    .getCoordinateAt(
      alongFromMember.lrRange.start / alongFromFeature.get("lengthCm"),
    );
  const toCoordinate = alongToFeature
    .getGeometry()
    .getCoordinateAt(
      alongToMember.lrRange.start / alongToFeature.get("lengthCm"),
    );
  const viaCoordinate = viaFeature.getGeometry().getCoordinates();
  return {
    status: "ready",
    from: { x: fromCoordinate[0], y: fromCoordinate[1] },
    to: { x: toCoordinate[0], y: toCoordinate[1] },
    via: { x: viaCoordinate[0], y: viaCoordinate[1] },
    curvature,
  };
}

// Build the arc Feature. Preserves the original asymmetry: a real curve copies
// ALL relation properties (then overrides geometry); a straight/dummy arc does NOT.
function buildArcFeature(relation, arc, curvature) {
  let feature;
  if (arc.fromCurve) {
    feature = new Feature({});
    feature.setProperties(relation.getProperties());
    feature.setGeometry(new LineString(arc.coordinates));
  } else {
    feature = new Feature({ geometry: new LineString(arc.coordinates) });
  }
  feature.set("type", arc.type);
  feature.set("relation-id", relation.getId());
  // The WKT row shows the relation's WFS geometry, not the computed arc.
  setSourceGeometry(feature, getSourceGeometry(relation));
  feature.set("curvature_at_junction", curvature);
  return feature;
}

function deriveArc(relation, indexById) {
  relation.set("is-relation", "yes"); // preserved as the original set it (output parity)
  const inputs = resolveArcInputs(relation, indexById);
  if (inputs.status === "missing") {
    return { status: "missing" };
  }
  const arc = computeArcCoordinates(
    inputs.from,
    inputs.to,
    inputs.via,
    inputs.curvature,
  );
  return {
    status: "derived",
    feature: buildArcFeature(relation, arc, inputs.curvature),
  };
}

function ensureState(source) {
  let state = STATE.get(source);
  if (!state) {
    state = { derivedIds: new Set(), deferredIds: new Set() };
    STATE.set(source, state);
    // refresh() (revision/auth change) → clear() → reset state + drop from scheduler.
    source.on("clear", () => {
      state.derivedIds.clear();
      state.deferredIds.clear();
      dirtySources.delete(source);
    });
  }
  return state;
}

function ensureSchedulerRunning() {
  if (rafHandle === null) {
    rafHandle = requestAnimationFrame(drainFrame);
  }
}

function drainFrame() {
  rafHandle = null;
  const deadline = performance.now() + FRAME_BUDGET_MS;
  const now = () => performance.now();
  for (const source of dirtySources) {
    const state = STATE.get(source);
    const { done } = runDerivationPass(source, state, {
      deriveArc,
      deadline,
      now,
    });
    if (done) {
      assignOverlapOffsets(source);
      dirtySources.delete(source);
    }
    if (performance.now() >= deadline) {
      break;
    }
  }
  if (dirtySources.size > 0) {
    rafHandle = requestAnimationFrame(drainFrame);
  }
}

// featuresloadend handler. New features may supply members that previously-deferred
// relations were waiting on, so re-arm this source's deferred set; then schedule.
export const curvatureAtJunctionArcHandler = (event) => {
  const source = event.target;
  const state = ensureState(source);
  state.deferredIds.clear();
  dirtySources.add(source);
  ensureSchedulerRunning();
};
